import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import TagManager from 'react-gtm-module'
import { t } from '@lingui/macro'
import { Honeybadger } from '@honeybadger-io/react'
// v6 is tree-shaken: the bare '@adyen/adyen-web' entry registers NO payment
// methods, so the Drop-in renders nothing. The '/auto' entry registers all
// components (v4 behaviour) so every method from /paymentMethods shows up.
import { AdyenCheckout, Dropin } from '@adyen/adyen-web/auto'
import '@adyen/adyen-web/styles/adyen.css'
import process from 'process'
import { useLocation, useHistory } from 'react-router-dom'
import { defaultLocale } from '../../../translations/i18n'
import paymentActions from '../../../redux-store/payments/payments.actions'
import ApiEndpoint, {
  ApiError,
  ApiResponse
} from '../../../network/ApiEndpoint'
import { PaymentContainerCloseButton } from '../PaymentContainerCloseButton/PaymentContainerCloseButton'
import { PaymentContainerRetryButton } from '../PaymentContainerRetryButton/PaymentContainerRetryButton'
import PAYMENTS from '../../../redux-store/payments/payments.constants'
import { AlertMessage } from '../../dialogs/AlertMessage/AlertMessage'
import styles from './PaymentContainer.module.scss'
import './adyenStyling.scss'
import { CustomText } from './CustomText/CustomText.interface'
import getCustomText from './CustomText/CustomText'
import { channelSelector } from '../../../redux-store/channel/channel.selectors'
import Modal from 'components/Modals/Modal/Modal'
import { TextSection } from './TextSection/TextSection'
import {
  PaymentResultView,
  PaymentResultStatus
} from './PaymentResultView/PaymentResultView'
import { AlertType } from 'features/viewerPage/components/StreamMessage/getAlertType'

export const PaymentContainer = () => {
  const paymentsOpen = useSelector((state: any) => state.payments.paymentsOpen)
  const currentPayment = useSelector(
    (state: any) => state.payments.currentPayment
  )
  const channel = useSelector(channelSelector)
  const paymentContainer = useRef<HTMLElement | null>(
    document.getElementById('payment-container')
  )
  const config: any = useRef(undefined)
  const checkout: any = useRef(undefined)
  const aDropin: any = useRef(undefined)
  // Guards the redirect-result reentry against duplicate submits (e.g. React
  // StrictMode double-invoking the effect), which would otherwise fire the
  // /payments/details call twice and 400 with "Already purchased".
  const redirectHandled: any = useRef(false)
  const dispatch = useDispatch()
  const language =
    sessionStorage.getItem('language') || navigator.language || defaultLocale
  const locale = language.substr(0, 2)

  const history = useHistory()
  const location = useLocation()
  const redirectParams: any = {
    result:
      new URLSearchParams(location.search).get('redirectResult') || undefined,
    media_object_id:
      new URLSearchParams(location.search).get('redirectMediaObjectId') ||
      undefined,
    subscription_id:
      new URLSearchParams(location.search).get('redirectSubscriptionId') ||
      undefined
  }

  const [errorMessage, setErrorMessage] = useState(<span />)
  const [redirectResult, setRedirectResult] = useState<{
    status: PaymentResultStatus
    title: string
    text: string
  } | null>(null)

  // TODO: Change to a helper function if supporting currencys with 0 or 3 decimals.
  const multiplicatorForCurrency = 100

  const removeRedirectParams = useCallback(() => {
    const queryParams = new URLSearchParams(location.search)
    queryParams.delete('redirectResult')
    queryParams.delete('redirectMediaObjectId')
    queryParams.delete('redirectSubscriptionId')
    history.replace({
      search: queryParams.toString()
    })
  }, [history, location.search])

  const closePaymentContainer = () => {
    setErrorMessage(<span />)
    setRedirectResult(null)
    redirectHandled.current = false
    if (aDropin.current) {
      aDropin.current.unmount()
    }
    dispatch(paymentActions.closePayment())
  }

  const retryPaymentContainer = () => {
    setErrorMessage(<span />)
    if (aDropin.current) {
      aDropin.current.setStatus('ready')
      dispatch(paymentActions.retryPayment())
    }
  }

  // Adyen Web v6 owns the Drop-in's final animation and action handling once we
  // call actions.resolve(). This helper only performs our own side effects
  // (Redux dispatches and error banners); the visual result/action is driven by
  // the resultCode/action we hand back to the SDK in resolveResult().
  const applyResultSideEffects = useCallback(
    (response: any) => {
      const data = response?.data
      if (!data) return
      let object_id: any
      switch (data.result_code) {
        case 'Authorised':
          switch (currentPayment.type) {
            case PAYMENTS.TYPE_PPV:
              object_id = currentPayment.mediaObjectId
              dispatch(paymentActions.paymentSuccess(object_id))
              break
            case PAYMENTS.TYPE_SUBSCRIPTION:
              object_id = currentPayment.subscription?.id
              dispatch(paymentActions.paymentSuccess(object_id))
              break
            case PAYMENTS.TYPE_ADD_CARD:
            default:
              object_id = data.ref_id
              dispatch(paymentActions.paymentSuccess(object_id))
          }

          if (data.type === 'gift_card') {
            // Show gift card result component after Adyen's success animation is ready
            setTimeout(() => {
              aDropin.current?.unmount()
              dispatch(paymentActions.hasGiftCard(data.ref_id))
              dispatch(paymentActions.closePayment())
            }, 1200)
          }
          break
        case 'Refused':
          setErrorMessage(
            <AlertMessage
              visible
              level={AlertType.Error}
              title={t`Error in payment`}
              text={t`The payment failed. Try again`}
            />
          )
          dispatch(paymentActions.paymentFailed())
          break
        case 'Pending':
        case 'PresentToShopper':
        case 'Received':
        case 'RedirectShopper':
        case 'ChallengeShopper':
        case 'IdentifyShopper':
          // Handled visually by the Drop-in via the resolved action / final state
          break
        case '':
        case 'Error':
          setErrorMessage(
            <AlertMessage
              visible
              level={AlertType.Error}
              title={t`Error`}
              text={t`The server returned ${data.result_code}`}
            />
          )
          break
        default:
          setErrorMessage(
            <AlertMessage
              visible
              level={AlertType.Warning}
              title={t`Something went wrong`}
              text={t`Try again`}
            />
          )
          break
      }
    },
    [currentPayment, dispatch]
  )

  // Hands the backend result back to the Drop-in. In v6 onSubmit/onAdditionalDetails
  // must settle via actions.resolve() (SDK then handles any action + final animation)
  // or actions.reject() to release the pay button into an error state.
  const resolveResult = useCallback(
    (response: any, actions: any) => {
      applyResultSideEffects(response)
      const resultCode = response?.data?.result_code
      if (resultCode) {
        actions.resolve({ resultCode, action: response.data.action })
      } else {
        actions.reject()
      }
    },
    [applyResultSideEffects]
  )

  // Handles the reentry after a redirect/3DS: the shopper comes back to the app
  // with a redirectResult in the URL. In the advanced flow the SDK's
  // submitDetails() only calls onAdditionalDetails (which posts to our backend),
  // so we call the backend directly instead of spinning up a Drop-in with no
  // clientKey/paymentMethods (that path handed onAdditionalDetails an undefined
  // component, crashing on component.setStatus).
  const handleRedirectResult = useCallback(async () => {
    if (redirectHandled.current) return
    redirectHandled.current = true
    const objectId =
      redirectParams.subscription_id || redirectParams.media_object_id
    try {
      const response: ApiResponse = await ApiEndpoint.call({
        path: 'payments/details',
        method: ApiEndpoint.METHODS.POST,
        params: {
          data: { details: { redirectResult: redirectParams.result } },
          return_path: '/',
          subscription_id: redirectParams.subscription_id || undefined,
          media_object_id: redirectParams.media_object_id || undefined
        },
        authenticatable: true
      })
      const resultCode = response?.data?.result_code
      if (resultCode === 'Authorised') {
        dispatch(paymentActions.paymentSuccess(objectId))
        setRedirectResult({
          status: 'success',
          title: t`Payment successful`,
          text: t`You now have access`
        })
      } else if (
        resultCode === 'Refused' ||
        resultCode === 'Error' ||
        !resultCode
      ) {
        setRedirectResult({
          status: 'error',
          title: t`Error in payment`,
          text: t`The payment failed. Try again`
        })
        dispatch(paymentActions.paymentFailed())
      }
      // Pending/PresentToShopper/Received: nothing to do here.
    } catch (error: any) {
      if (error?.message === 'Already purchased') {
        dispatch(paymentActions.alreadyPurchased(objectId))
        setRedirectResult({
          status: 'success',
          title: t`Already purchased`,
          text: t`You already have access to this`
        })
      } else {
        console.error('Payment error handling redirect result', error)
        setRedirectResult({
          status: 'error',
          title: t`Error`,
          text: t`Something went wrong. Try again`
        })
        dispatch(paymentActions.paymentFailed())
      }
    } finally {
      removeRedirectParams()
    }
  }, [
    redirectParams.result,
    redirectParams.subscription_id,
    redirectParams.media_object_id,
    dispatch,
    removeRedirectParams
  ])

  const showPayment = useCallback(async () => {
    // Redirect/3DS reentry is resolved via our backend directly, no Drop-in.
    if (currentPayment.type === PAYMENTS.TYPE_REDIRECT_RESULT) {
      if (paymentContainer.current) {
        paymentContainer.current.classList.remove('hidden')
      }
      await handleRedirectResult()
      return
    }
    if (currentPayment.type !== PAYMENTS.TYPE_REDIRECT_RESULT) {
      // commented out for now, we need to add back later
      // when Tag Manager specs are better defined
      /*
      TagManager.dataLayer({
        dataLayer: {
          event: 'begin_checkout',
          currency: currentPayment.currency,
          items: currentPayment.type,
          value: currentPayment.value
        }
      })
      */
      /* returns...
      {
            "type": "TYPE_PPV",
            "value": 29,
            "currency": "SEK",
            "mediaObjectId": "kxxb1pxd",
            "subscription": {
                "price": {
                    "SEK": 29
                }
            }
      }
      */
    }
    if (paymentContainer && paymentContainer.current) {
      paymentContainer.current.classList.remove('hidden')
    }
    const betterLocale = `${locale}-${config.current?.country_code || 'US'}`
    let path: string
    let return_path: string
    let translations: CustomText | undefined
    let freeDays: number | undefined
    switch (currentPayment.type) {
      case PAYMENTS.TYPE_PPV:
        path = 'payments/ppv'
        return_path = ''
        return_path = `${window.location.pathname}${
          currentPayment?.mediaObjectId
            ? `?redirectMediaObjectId=${currentPayment.mediaObjectId}`
            : ''
        }`
        break
      case PAYMENTS.TYPE_SUBSCRIPTION:
        path = 'payments/subscription'
        return_path = `${window.location.pathname}${
          currentPayment?.subscription?.id
            ? `?redirectSubscriptionId=${currentPayment.subscription.id}`
            : ''
        }`
        freeDays = currentPayment.subscription?.free_days
        break
      case PAYMENTS.TYPE_ADD_CARD:
        path = 'users/credit_cards/add_card'
        translations = getCustomText(PAYMENTS.TYPE_ADD_CARD, betterLocale)
        return_path = `${window.location.pathname}${'?add_card=true'}`
        break
      default:
    }

    const utm_source = sessionStorage.getItem('utm_source')

    // v6: instance-level config (payment methods response, keys, callbacks) goes
    // to AdyenCheckout(); component-level config goes to new Dropin() below.
    const coreConfiguration = {
      paymentMethodsResponse: config.current?.method,
      clientKey: config.current?.client,
      locale: betterLocale,
      // v6: countryCode is mandatory on the AdyenCheckout instance.
      countryCode: config.current?.country_code || 'US',
      translations: translations,
      amount: {
        value: Number(
          freeDays && freeDays > 0
            ? 0
            : (currentPayment.value * multiplicatorForCurrency).toPrecision(10)
        ),
        currency: currentPayment.currency
      },
      environment: process.env.REACT_APP_ADYEN_CHECKOUT_ENVIRONMENT as
        | 'test'
        | 'live'
        | 'live-us'
        | 'live-au'
        | 'live-apse'
        | 'live-in'
        | 'live-nea',
      onSubmit: async (state: any, component: any, actions: any) => {
        // Your function calling your server to make the `/payments` request
        try {
          const response: ApiResponse = await ApiEndpoint.call({
            path,
            method: ApiEndpoint.METHODS.POST,
            params: {
              origin: window.location.origin,
              media_object_id:
                currentPayment.type === PAYMENTS.TYPE_PPV
                  ? currentPayment.mediaObjectId
                  : undefined,
              subscription_id:
                currentPayment.type === PAYMENTS.TYPE_SUBSCRIPTION
                  ? currentPayment.subscription.id
                  : undefined,
              data: state.data,
              value: currentPayment.value,
              currency: currentPayment.currency,
              return_path: return_path,
              gateway: 'adyen',
              origin_company: window.location.pathname.split('/')[1],
              utm_source
            },
            authenticatable: true
          })

          if (response.data?.result_code === 'Authorised') {
            const channelData = channel?.data
            const dataLayer = {
              dataLayer: {
                event: 'purchase',
                purchase_type: currentPayment.type, // All Access, Monthly Subscription, PPV
                channel_name: channelData?.name,
                sport_category: channelData?.category?.slug,
                powered_by: channelData?.isRootChannel
                  ? channelData?.subdomain
                  : channelData?.parentChannelSubdomain,
                item_name: currentPayment.mediaObjectTitle,
                value: currentPayment.value,
                currency: currentPayment.currency,
                transaction_id: currentPayment.mediaObjectId
              }
            }
            TagManager.dataLayer(dataLayer)
          }

          // v6: hand the result back to the Drop-in, which handles any action
          // (3DS/redirect) and the final success/error animation itself.
          resolveResult(response, actions)
        } catch (error: any) {
          console.warn('Error in payments call:', error)
          // TODO: Change check to error.code when error.code is available
          if (error?.message === 'Already purchased') {
            dispatch(
              paymentActions.alreadyPurchased(
                currentPayment.type === PAYMENTS.TYPE_PPV
                  ? currentPayment.mediaObjectId
                  : currentPayment.subscription_id
              )
            )
            component.setStatus('success', {
              message: t`You have already purchased this item`
            })
          } else {
            console.error('Payment error in onSubmit', error)
            dispatch(paymentActions.paymentFailed())
            actions.reject()
          }
        }
      },
      onAdditionalDetails: async (state: any, component: any, actions: any) => {
        const params: any = {
          data: state.data,
          return_path: '/'
        }
        switch (currentPayment.type) {
          case PAYMENTS.TYPE_PPV:
            params.media_object_id = currentPayment.mediaObjectId
            break
          case PAYMENTS.TYPE_SUBSCRIPTION:
            params.subscription_id = currentPayment.subscription?.id
            break
          case PAYMENTS.TYPE_REDIRECT_RESULT:
            params.subscription_id = redirectParams.subscription_id || undefined
            params.media_object_id = redirectParams.media_object_id || undefined
            break
          default:
            break
        }
        try {
          const response: ApiResponse = await ApiEndpoint.call({
            path: 'payments/details',
            method: ApiEndpoint.METHODS.POST,
            params,
            authenticatable: true
          })
          resolveResult(response, actions)
        } catch (error: any) {
          // TODO: Change check to error.code when error.code is available
          if (error?.message === 'Already purchased') {
            dispatch(
              paymentActions.alreadyPurchased(
                currentPayment.type === PAYMENTS.TYPE_PPV
                  ? currentPayment.mediaObjectId
                  : currentPayment.subscription_id
              )
            )
            // Native 3DS: component is defined. Redirect reentry is handled in
            // handleRedirectResult(), so it never reaches here.
            component?.setStatus('success', {
              message: t`You have already purchased this item`
            })
          } else {
            console.error('Payment error in onAdditionalDetails', error)
            actions.reject()
          }
        } finally {
          removeRedirectParams()
        }
      },
      onError: (error: any) => {
        if (error.error !== '') {
          dispatch(paymentActions.paymentFailed())
        } else {
          dispatch(paymentActions.paymentOngoing())
        }
      }
    }

    // v6: Drop-in component configuration.
    const dropinConfiguration = {
      paymentMethodsConfiguration: {
        card: {
          hasHolderName: true,
          holderNameRequired: true,
          enableStoreDetails: currentPayment.type !== PAYMENTS.TYPE_ADD_CARD
        }
      },
      showStoredPaymentMethods: currentPayment.type !== PAYMENTS.TYPE_ADD_CARD, // TODO: This does not seem to work for some reason.
      showRemovePaymentMethodButton: true
      // NOTE: do NOT dispatch closePayment() from the Drop-in onReady callback.
      // In v4 onReady lived on the core config and never fired for Drop-in, so it
      // was dead code. In v6 onReady is a Drop-in callback that fires on mount, and
      // closePayment() closes the modal (and trips PurchaseContainer's redirect),
      // which instantly dismisses the payment before the shopper can pay.
    }

    try {
      // v6: AdyenCheckout is now async and returns the checkout instance.
      checkout.current = await AdyenCheckout(coreConfiguration)
    } catch (error) {
      console.error('Failed to initialise AdyenCheckout', error)
      Honeybadger.notify(`Failed to initialise AdyenCheckout: ${error}`)
      setErrorMessage(
        <AlertMessage
          visible
          level={AlertType.Error}
          title={t`Error`}
          text={t`We could not load the payment methods. Try again`}
        />
      )
      dispatch(paymentActions.paymentFailed())
      return
    }

    // v6: Drop-in is created via its constructor instead of checkout.create('dropin').
    aDropin.current = new Dropin(
      checkout.current,
      dropinConfiguration
    ).mount('#dropin-container')
  }, [
    locale,
    redirectParams.media_object_id,
    redirectParams.subscription_id,
    dispatch,
    currentPayment,
    removeRedirectParams,
    resolveResult,
    handleRedirectResult,
    channel
  ])

  useEffect(() => {
    if (!paymentContainer.current) {
      paymentContainer.current = document.getElementById('payment-container')
    }
    if (paymentsOpen) {
      if (
        currentPayment.value !== undefined ||
        currentPayment.type === PAYMENTS.TYPE_ADD_CARD
      ) {
        let params: any = {}
        if (currentPayment.type !== PAYMENTS.TYPE_ADD_CARD) {
          params = {
            currency: currentPayment.currency,
            value: currentPayment.value
          }
        }
        if (currentPayment.type === PAYMENTS.TYPE_SUBSCRIPTION) {
          params.subscription_id = currentPayment.subscription?.id
        } else if (currentPayment.type === PAYMENTS.TYPE_PPV) {
          params.media_object_id = currentPayment.mediaObjectId
        } else if (currentPayment.type === PAYMENTS.TYPE_ADD_CARD) {
          params.add_card = true
        }

        ApiEndpoint.call({
          path: 'payments/methods',
          method: ApiEndpoint.METHODS.GET,
          params,
          authenticatable: true
        })
          .then((response: ApiResponse) => {
            // Errors from Adyen are not returned as errors
            if (response?.data?.method?.errorCode !== undefined) {
              setErrorMessage(
                <AlertMessage
                  visible
                  level={AlertType.Error}
                  title={t`Error`}
                  text={t`We could not load the payment methods. Try again`}
                />
              )
              dispatch(paymentActions.paymentFailed())
              console.warn(response?.data?.method)
            }
            config.current = response?.data
            try {
              config.current.method = JSON.parse(response?.data.method)
            } catch {
              config.current.method = response?.data.method
            }
            showPayment()
          })
          .catch((error: ApiError) => {
            // TODO: Change check to error.code when error.code is available
            if (error.message === 'Already purchased') {
              // this is the code that triggers aggain
              setErrorMessage(
                <AlertMessage
                  visible
                  level={AlertType.Info}
                  title={t`Already purchased`}
                  text={t`You already have access to this`}
                />
              )
              dispatch(
                paymentActions.alreadyPurchased(
                  currentPayment.type === PAYMENTS.TYPE_PPV
                    ? currentPayment.mediaObjectId
                    : currentPayment.subscription_id
                )
              )
            } else {
              setErrorMessage(
                <AlertMessage
                  visible
                  level={AlertType.Error}
                  title={t`Error`}
                  text={t`We could not load the payment methods. Try again`}
                />
              )
              dispatch(paymentActions.paymentFailed())
              console.warn(error)
            }
          })
      } else if (currentPayment.type === PAYMENTS.TYPE_REDIRECT_RESULT) {
        setTimeout(showPayment, 1200)
      }
    }
  }, [paymentsOpen, currentPayment, dispatch, showPayment])

  useEffect(() => {
    if (redirectParams.result && !paymentsOpen) {
      dispatch(
        paymentActions.openPayment({
          type: PAYMENTS.TYPE_REDIRECT_RESULT,
          redirectResult: redirectParams.result
        })
      )
    }
  }, [redirectParams.result, paymentsOpen, dispatch])

  if (paymentsOpen) {
    // Make sure that the container is visible
    try {
      window.scrollTo(0, 0)
    } catch (e) {
      // Ignore for now
    }
  }

  /* if (!paymentsOpen) {
    return <div id="payment-container" className="popup-container hidden" />
  } */

  return (
    <Modal
      isOpen={paymentsOpen}
      handleClose={() => closePaymentContainer()}
      customStyle={styles.ModalContainer}
    >
      <div id="payment-container" className={styles.PaymentContent}>
        {redirectResult ? (
          <PaymentResultView
            status={redirectResult.status}
            title={redirectResult.title}
            text={redirectResult.text}
            onClose={closePaymentContainer}
          />
        ) : (
          <>
            <TextSection currentPayment={currentPayment} />
            {errorMessage}
            <div
              data-testid="payment-container"
              id="dropin-container"
              className={styles.Payments}
            />
            <div className={styles.ButtonContainer}>
              <PaymentContainerCloseButton
                closePaymentContainer={closePaymentContainer}
              />
              <PaymentContainerRetryButton
                retryPaymentContainer={retryPaymentContainer}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
